import React, {
    FC,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import FilterListIcon from '@material-ui/icons/FilterList'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import PromptContext from '../../../../React/Common/Contexts/PromptContext'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues, AdminFiltersSlotRenderArgs } from '../../Molecules/AdminFilters/AdminFilters'
import { SelectFilter, SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { SelectFilterMulti } from '../../Molecules/AdminFilters/SelectFilterMulti'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { COMMA, EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { TRUE } from '../../../../NonReact/constants/Common'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn, DataTableToolbarCustomAction } from '../../Molecules/DataTable/DataTable'
import { IconName } from '../../../../React/Common/Components/Icon'
import { displayError, displayErrorMessage } from '../../../../React/Common/Utils/Messages'
import {
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage,
    UsersListLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import BulkInsertUsers from '../../../../React/BulkInsertUsers'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getUsersList } from '../../../../React/Common/API/ItemsGetters'
import {
    UsersList as UsersListRows,
    UsersListItem,
    UsersListResponse,
    UsersListRow
} from '../../../../NonReact/Types/UsersListTypes'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import AddUserModal from '../../../../React/AddUser/AddUserModal'
import { CopyReportsToWizardModal } from './CopyReportsToWizardModal'
import { UsersListMoreInformation } from './UsersListMoreInformation'
import { adminSignInAsUser, deleteUser, restoreUser } from '../../../../NonReact/common/API/EntityAPIClient'
import { notifyFusionHostOfSignInAsChange } from '../../../../NonReact/common/FusionReauthBridge'
import { isFusionFramed } from '../../../../NonReact/common/FusionThemeBridge'
import { AdminListUrlBootstrap, parseAdminListUrlSearchParams } from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../Utils/adminListFilterReadiness'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { APIElements } from '../../../../NonReact/constants/ApiConstants'
import { redirectToServletWithBranding, ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { LanguageUtils } from '../../../../React/Common/Utils/LanguageUtils'

const SERVLET_CMD_PREFIX: string = 'CMD_'

/**
 * Normalises the servlet command returned after sign-in-as (ensures a `CMD_` prefix when the API omits it).
 *
 * @param redirectRaw - Optional redirect command from the API.
 * @returns Servlet command suitable for {@link redirectToServletWithBranding}.
 */
const normaliseSignInAsRedirectCommand = (redirectRaw: Definable<string>): string => {
    if (!isStringNotEmpty(redirectRaw)) return ServletCmds.MAIN_HOME

    if (redirectRaw.startsWith(SERVLET_CMD_PREFIX)) return redirectRaw

    return `${SERVLET_CMD_PREFIX}${redirectRaw}`
}

/**
 * True when the filter value is a concrete TelSys or partition selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony or partition option value from admin filters.
 * @returns Whether the value should be sent to the user list API as a restricting parameter.
 */
const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) return false

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

/**
 * Reads a boolean field that the admin list API may publish as either a real boolean or its string form.
 *
 * @param value - Raw field value from the API.
 * @returns Whether the flag is set.
 */
function isFlagSet(value: Definable<boolean | string>): boolean {
    if (typeof value === 'boolean') return value

    return isStringNotEmpty(value) && value.toLowerCase() === TRUE
}

/**
 * Parses a lock-out field returned by the admin user list API into a stable display string.
 *
 * @param lockOut - Raw lock-out value from the API.
 * @param yesLabel - Localised "yes" label.
 * @param noLabel - Localised "no" label.
 * @returns Localised lock-out display text.
 */
const formatLockOutDisplay = (lockOut: Definable<boolean | string>, yesLabel: string, noLabel: string): string =>
    isFlagSet(lockOut) ? yesLabel : noLabel

/**
 * Removes one surrounding pair of square brackets from backend list values while leaving inner content untouched.
 *
 * @param value - Raw backend display value.
 * @returns Value without one outer `[]` pair when present.
 */
const stripSurroundingSquareBrackets = (value: Definable<string>): string => {
    if (!isStringNotEmpty(value)) return EMPTY_STRING

    if (value.startsWith('[') && value.endsWith(']')) {
        return value.substring(1, value.length - 1)
    }

    return value
}

/**
 * Routed Application Users list view.
 *
 * @returns Application Users list page content.
 */
/** Locked-out filter option values; the request sends a boolean, so these only identify the choice. */
const LOCKED_OUT_YES = 'lockedOut'
const LOCKED_OUT_NO = 'notLockedOut'

/** Pending-deletion filter option values; as above, these only identify the choice. */
const PENDING_DELETION_ONLY = 'pendingDeletionOnly'
const PENDING_DELETION_EXCLUDED = 'pendingDeletionExcluded'

/**
 * Joins a multi-select filter's chosen values into the comma separated list the list API expects.
 *
 * @param selected - Options currently chosen for one filter.
 * @returns The comma separated values, or undefined when nothing is chosen and the filter should be omitted.
 */
function joinFilterValues(selected: readonly SelectFilterOption[]): Definable<string> {
    if (ArrayUtils.isEmpty([...selected])) return undefined

    return selected.map((option: SelectFilterOption): string => String(option.value)).join(COMMA)
}

/**
 * Resolves the locked-out filter choice into the boolean the list API expects.
 *
 * @param selected - The chosen option, or null.
 * @returns True or false to restrict to that state, or undefined to accept either.
 */
function resolveLockedOutFilter(selected: Nullable<SelectFilterOption>): Definable<boolean> {
    if (IsVarEmpty(selected) || selected.value === ALL_VALUE) return undefined

    return selected.value === LOCKED_OUT_YES
}

/**
 * Resolves the pending-deletion filter choice into the boolean the list API expects.
 *
 * Pending deletion users are listed alongside everyone else by default, so the unset choice has to leave the
 * filter off the request rather than send false.
 *
 * @param selected - The chosen option, or null.
 * @returns True to list only pending deletion users, false to exclude them, undefined to list both.
 */
function resolvePendingDeletionFilter(selected: Nullable<SelectFilterOption>): Definable<boolean> {
    if (IsVarEmpty(selected) || selected.value === ALL_VALUE) return undefined

    return selected.value === PENDING_DELETION_ONLY
}

export const UsersList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(UsersListLanguage.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [usersList, setUsersList]: ReactState<Nullable<UsersListRows>> = useState<Nullable<UsersListRows>>()
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const { displayPromptPromise } = useContext(PromptContext)
    const branding: Nullable<Branding> = useBranding()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [userTypeFilters, setUserTypeFilters]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [licenceFilters, setLicenceFilters]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [activityFilters, setActivityFilters]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [lockedOutFilter, setLockedOutFilter]: ReactState<Nullable<SelectFilterOption>> =
        useState<Nullable<SelectFilterOption>>(null)
    const [pendingDeletionFilter, setPendingDeletionFilter]: ReactState<Nullable<SelectFilterOption>> =
        useState<Nullable<SelectFilterOption>>(null)
    const [isUserModalOpen, setUserModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedUsername, setSelectedUsername]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [userModalViewOnly, setUserModalViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [copyReportsSourceUser, setCopyReportsSourceUser]: ReactState<Definable<UsersListRow>> =
        useState<Definable<UsersListRow>>()
    const [isBulkInsertOpen, setBulkInsertOpen]: ReactState<boolean> = useState<boolean>(false)

    const canSignInAs: boolean = useMemo(
        (): boolean => userInfo?.Permissions?.Admin?.includes(AdminBackendPermissionNames.SIGN_IN_AS) ?? false,
        [userInfo]
    )

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const cmeFilterValue: Definable<string | number> = filters?.cme?.value
    const listCmeId: Nullable<string> = IsVarEmpty(cmeFilterValue)
        ? userInfo?.Scope?.CMEnvironmentID ?? null
        : String(cmeFilterValue)
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const telSysFilterValue: Definable<string | number> = filters?.telephonyServer?.value
    const partitionFilterValue: Definable<string | number> = filters?.partition?.value
    const listTelSysId: Definable<string | number> = isConcreteTelSysOrPartitionFilterValue(telSysFilterValue)
        ? telSysFilterValue
        : ALL_VALUE
    const listPartIDInTelSys: Definable<string | number> =
        isConcreteTelSysOrPartitionFilterValue(telSysFilterValue) &&
        isConcreteTelSysOrPartitionFilterValue(partitionFilterValue)
            ? partitionFilterValue
            : ALL_VALUE
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_USERS, filters, userInfo?.Scope?.CMEnvironmentID)

    const listUserTypeFilter: Definable<string> = joinFilterValues(userTypeFilters)
    const listLicenceFilter: Definable<string> = joinFilterValues(licenceFilters)
    const listActivityFilter: Definable<string> = joinFilterValues(activityFilters)
    const listLockedOutFilter: Definable<boolean> = resolveLockedOutFilter(lockedOutFilter)
    const listPendingDeletionFilter: Definable<boolean> = resolvePendingDeletionFilter(pendingDeletionFilter)

    const filterCMEnvironmentIDForModal: string = String(
        filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID
    )

    const modalFilterTelSysID: Definable<number> = isConcreteTelSysOrPartitionFilterValue(telSysFilterValue)
        ? Number(telSysFilterValue)
        : undefined

    const modalFilterPartitionID: Definable<number> =
        isConcreteTelSysOrPartitionFilterValue(telSysFilterValue) &&
        isConcreteTelSysOrPartitionFilterValue(partitionFilterValue)
            ? Number(partitionFilterValue)
            : undefined

    const bulkInsertServletPath: string = useMemo((): string => {
        const trimmedMapping: string = (branding?.URLMapping ?? EMPTY_STRING).replace(/\//g, EMPTY_STRING)

        return isStringNotEmpty(trimmedMapping) ? trimmedMapping : APIElements.AKIXI_2019
    }, [branding?.URLMapping])

    /**
     * Fetches application users for the current filter state.
     *
     * @returns No return value.
     */
    const getApplicationUsers = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listCmeId)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        if (listNoTelephonyServer) {
            if (isMounted.current) {
                setUsersList([])
                setTotalCount(0)
            }
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            setLoading((currentLoading: number): number => currentLoading - 1)
            return
        }

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        getUsersList(
            listTelSysId ?? ALL_VALUE,
            listPartIDInTelSys ?? ALL_VALUE,
            listCmeId,
            listIncludeRecursive,
            pageStartIndex,
            rowsPerPage,
            sort,
            searchQuery,
            listUserTypeFilter,
            listLicenceFilter,
            listActivityFilter,
            listLockedOutFilter,
            listPendingDeletionFilter
        )
            .then((response: Nullable<UsersListResponse>): void => {
                if (!isMounted.current) return

                const rows: UsersListRows =
                    response?.Data?.Items?.map(
                        (item: UsersListItem, index: number): UsersListRow => ({
                            key: buildAdminListRowKey(
                                [
                                    listCmeId,
                                    listTelSysId,
                                    listPartIDInTelSys,
                                    stripSurroundingSquareBrackets(item.Username),
                                    item.FullName
                                ],
                                pageStartIndex,
                                index
                            ),
                            Description: item.FullName ?? EMPTY_STRING,
                            Username: stripSurroundingSquareBrackets(item.Username),
                            FullName: item.FullName ?? EMPTY_STRING,
                            LockOutDisplay: formatLockOutDisplay(
                                item.LockOut,
                                language.get(UsersListLanguage.UsersList_LockOut_Yes),
                                language.get(UsersListLanguage.UsersList_LockOut_No)
                            ),
                            IsPendingDeletion: isFlagSet(item.PendingDeletion),
                            ListActivity: stripSurroundingSquareBrackets(item.ListActivity),
                            ListLastLogon: stripSurroundingSquareBrackets(item.ListLastLogon),
                            ListActiveSignIns: item.ListActiveSignIns ?? EMPTY_STRING,
                            ListPermissionsSummary: stripSurroundingSquareBrackets(item.ListPermissionsSummary),
                            ListTelSysDescription: stripSurroundingSquareBrackets(item.ListTelSysDescription)
                        })
                    ) ?? []

                setUsersList(rows)
                setTotalCount(Number(response?.Data?.Total) || 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)
                setHasResolvedInitialFiltersLoad(true)

                if (!isMounted.current) return

                setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listCmeId,
        listIncludeRecursive,
        listNoTelephonyServer,
        listPartIDInTelSys,
        listSearch,
        listTelSysId,
        listUserTypeFilter,
        listLicenceFilter,
        listActivityFilter,
        listLockedOutFilter,
        listPendingDeletionFilter,
        pagination,
        sort,
        userInfo?.Scope?.CMEnvironmentID
    ])

    const refreshUsersList = useCallback((): void => {
        getApplicationUsers()
    }, [getApplicationUsers])

    const handleToolbarSignInAsClick = useCallback(
        ([selectedItem]: UsersListRow[]): void => {
            displayPromptPromise(
                LanguageUtils.formatLanguageEntry(
                    language.getInterpolated(UsersListLanguage.UsersList_SignInAs_Confirm, selectedItem.Username)
                )
            )
                .then((): Promise<void> => {
                    setLoading((currentLoading: number): number => currentLoading + 1)

                    return adminSignInAsUser(selectedItem.Username)
                        .then(async (response): Promise<void> => {
                            // Embedded in the new UI: skip the legacy in-iframe redirect entirely.
                            // The server session is now impersonating; hand it up to the host, which
                            // adopts the new user's token and routes to the realtime dashboard as
                            // that user (CXUI-402).
                            if (isFusionFramed()) {
                                await notifyFusionHostOfSignInAsChange()

                                return
                            }

                            const command: string = normaliseSignInAsRedirectCommand(response?.Data?.RedirectThePageTo)

                            redirectToServletWithBranding(command, branding?.URLMapping ?? APIElements.AKIXI_2019)
                        })
                        .catch((error: Error): void => {
                            displayError(error)
                        })
                        .finally((): void => {
                            if (!isMounted.current) return

                            setLoading((currentLoading: number): number => currentLoading - 1)
                        })
                })
                .catch((): void => {
                    // User cancelled the confirmation prompt.
                })
        },
        [branding?.URLMapping, displayPromptPromise, isMounted, language]
    )

    /**
     * Deletes the selected users, matching the classic page's two-stage delete.
     *
     * A licensed user is moved to the pending deletion state on the first delete; deleting them again is what
     * removes them for good. Mixing the two states in one selection is refused before the confirmation prompt
     * rather than guessed at, because the same button would then mean "suspend" for some of the rows and
     * "destroy" for the rest.
     *
     * @param selectedItems - Rows checked in the table.
     */
    const handleToolbarDeleteClick = useCallback(
        (selectedItems: UsersListRow[]): void => {
            if (ArrayUtils.isEmpty(selectedItems)) return

            // The mixed selection is already refused before the prompt, so every row here is in the same state.
            // Asking for all of them rather than any of them keeps that safe if it ever is not: a permanent delete
            // of a user who is not awaiting deletion is not refused by the server, it just removes them.
            const isPermanent: boolean = selectedItems.every((row: UsersListRow): boolean => row.IsPendingDeletion)

            setLoading((currentLoading: number): number => currentLoading + 1)
            Promise.all(
                selectedItems.map((row: UsersListRow): Promise<unknown> => deleteUser(row.Username, isPermanent))
            )
                .catch((error: Error): void => {
                    displayError(error)
                })
                .finally((): void => {
                    // Refreshed whatever the outcome: one rejection out of several still leaves the rows that did
                    // succeed showing their old state, and acting on those again would target the wrong stage.
                    getApplicationUsers()

                    if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
                })
        },
        [getApplicationUsers, isMounted]
    )

    /**
     * Refuses a selection that mixes users awaiting deletion with active ones, before anything is confirmed.
     *
     * The same button would otherwise mean "suspend" for some of the rows and "destroy" for the rest. The classic
     * page refuses up front too, so the check belongs here rather than in the click handler - raising it after the
     * confirmation prompt would ask the user to confirm a destructive-sounding action only to then refuse it.
     *
     * @param selectedItems - Rows checked in the table.
     * @returns Whether the delete may go ahead.
     */
    const canToolbarDelete = useCallback(
        (selectedItems: UsersListRow[]): boolean => {
            const pendingCount: number = selectedItems.filter(
                (row: UsersListRow): boolean => row.IsPendingDeletion
            ).length

            if (pendingCount > 0 && pendingCount < selectedItems.length) {
                displayErrorMessage(language.get(UsersListLanguage.UsersList_Delete_MixedPendingDeletion))

                return false
            }

            return true
        },
        [language]
    )

    /**
     * Takes the selected users back out of the pending deletion state.
     *
     * @param selectedItems - Rows checked in the table, all of which are pending deletion.
     */
    const handleToolbarRestoreClick = useCallback(
        (selectedItems: UsersListRow[]): void => {
            if (ArrayUtils.isEmpty(selectedItems)) return

            const confirmation: ReactElement =
                selectedItems.length === 1
                    ? LanguageUtils.formatLanguageEntry(
                          language.getInterpolated(
                              UsersListLanguage.UsersList_Restore_Confirm,
                              selectedItems[0].Username
                          )
                      )
                    : LanguageUtils.formatLanguageEntry(
                          language.getInterpolated(
                              UsersListLanguage.UsersList_Restore_ConfirmMany,
                              String(selectedItems.length)
                          )
                      )

            displayPromptPromise(confirmation)
                .then((): void => {
                    setLoading((currentLoading: number): number => currentLoading + 1)
                    Promise.all(selectedItems.map((row: UsersListRow): Promise<unknown> => restoreUser(row.Username)))
                        .catch((error: Error): void => {
                            displayError(error)
                        })
                        .finally((): void => {
                            // Refreshed whatever the outcome - see the delete handler for why a partial failure
                            // still has to re-read the list.
                            getApplicationUsers()

                            if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
                        })
                })
                .catch((): void => {
                    // User cancelled the confirmation prompt.
                })
        },
        [displayPromptPromise, getApplicationUsers, isMounted, language]
    )

    const toolbarCustomActions: DataTableToolbarCustomAction<UsersListRow>[] =
        useMemo((): DataTableToolbarCustomAction<UsersListRow>[] => {
            const actions: DataTableToolbarCustomAction<UsersListRow>[] = [
                {
                    label: language.get(UsersListLanguage.UsersList_CopyReports_Button),
                    icon: IconName.COPY,
                    isVisible: (count: number): boolean => count === 1,
                    requiresUpdatePermission: true,
                    onClick: ([selectedItem]: UsersListRow[]): void => {
                        setCopyReportsSourceUser(selectedItem)
                    }
                }
            ]

            // Restoring only means anything for a user awaiting deletion, so the control stays hidden until the
            // whole selection is in that state - which also keeps it off the toolbar for most selections.
            actions.push({
                label: language.get(UsersListLanguage.UsersList_Restore_Button),
                icon: IconName.RESTORE_FROM_TRASH,
                isVisible: (count: number, selectedItems: UsersListRow[]): boolean =>
                    !ArrayUtils.isEmpty(selectedItems) &&
                    selectedItems.every((row: UsersListRow): boolean => row.IsPendingDeletion),
                requiresUpdatePermission: true,
                onClick: handleToolbarRestoreClick
            })

            if (canSignInAs) {
                actions.push({
                    label: language.get(UsersListLanguage.UsersList_SignInAs_Button),
                    icon: IconName.PERSON,
                    isVisible: (count: number): boolean => count === 1,
                    requiresUpdatePermission: false,
                    onClick: handleToolbarSignInAsClick
                })
            }

            return actions
        }, [canSignInAs, handleToolbarRestoreClick, handleToolbarSignInAsClick, language])

    // The classic user list offers all of these in one "Show:" dropdown. Splitting them into the dimensions they
    // actually describe is what makes the filters legible, and each keeps the classic page's own wording.
    const userTypeOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: '1', label: language.get(UsersListLanguage.UsersList_UserType_ServiceWide) },
            { value: '2', label: language.get(UsersListLanguage.UsersList_UserType_SystemWide) },
            { value: '10', label: language.get(UsersListLanguage.UsersList_UserType_Detached) },
            { value: '20', label: language.get(UsersListLanguage.UsersList_UserType_AdminInclusive) },
            { value: '21', label: language.get(UsersListLanguage.UsersList_UserType_AdminExclusive) },
            { value: '22', label: language.get(UsersListLanguage.UsersList_UserType_EptAccess) },
            { value: '98', label: language.get(UsersListLanguage.UsersList_UserType_CxIntegration) }
        ],
        [language]
    )

    const licenceOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: '30', label: language.get(UsersListLanguage.UsersList_UserType_ReportingHistoricOnly) },
            { value: '31', label: language.get(UsersListLanguage.UsersList_UserType_ReportingBLF) },
            { value: '32', label: language.get(UsersListLanguage.UsersList_UserType_ReportingPersonalDashboard) },
            { value: '33', label: language.get(UsersListLanguage.UsersList_UserType_ReportingStd) },
            { value: '34', label: language.get(UsersListLanguage.UsersList_UserType_ReportingACD) },
            { value: '35', label: language.get(UsersListLanguage.UsersList_UserType_ReportingOmni) },
            { value: '36', label: language.get(UsersListLanguage.UsersList_UserType_ReportingMobileAppAdvanced) }
        ],
        [language]
    )

    const activityOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: '41', label: language.get(UsersListLanguage.UsersList_UserType_SignedInNow) },
            { value: '42', label: language.get(UsersListLanguage.UsersList_UserType_SignedInToday) },
            { value: '43', label: language.get(UsersListLanguage.UsersList_UserType_SignedInThisWeek) },
            { value: '45', label: language.get(UsersListLanguage.UsersList_UserType_SignedInThisMonth) },
            { value: '50', label: language.get(UsersListLanguage.UsersList_UserType_SignedInNever) },
            { value: '60', label: language.get(UsersListLanguage.UsersList_UserType_ActivityToday) },
            { value: '61', label: language.get(UsersListLanguage.UsersList_UserType_ActivityThisWeek) },
            { value: '63', label: language.get(UsersListLanguage.UsersList_UserType_ActivityThisMonth) },
            { value: '70', label: language.get(UsersListLanguage.UsersList_UserType_ActivityNone) }
        ],
        [language]
    )

    const lockedOutOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: ALL_VALUE, label: language.get(UsersListLanguage.UsersList_LockedOut_Any) },
            { value: LOCKED_OUT_YES, label: language.get(UsersListLanguage.UsersList_LockedOut_Yes) },
            { value: LOCKED_OUT_NO, label: language.get(UsersListLanguage.UsersList_LockedOut_No) }
        ],
        [language]
    )

    const pendingDeletionOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: ALL_VALUE, label: language.get(UsersListLanguage.UsersList_PendingDeletion_Any) },
            { value: PENDING_DELETION_ONLY, label: language.get(UsersListLanguage.UsersList_PendingDeletion_Only) },
            {
                value: PENDING_DELETION_EXCLUDED,
                label: language.get(UsersListLanguage.UsersList_PendingDeletion_Excluded)
            }
        ],
        [language]
    )

    const columns: DataTableColumn<UsersListRow, keyof UsersListRow>[] = useMemo((): DataTableColumn<
        UsersListRow,
        keyof UsersListRow
    >[] => {
        const lastLogonNoneLabel: string = language.get(UsersListLanguage.UsersList_LastLogon_None)

        return [
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_Username),
                field: 'Username',
                key: 'users-list-username',
                sortable: true,
                align: 'left',
                // Pending deletion has no column of its own in the agreed design, but without some mark on the row
                // a suspended user is indistinguishable from an active one - and then Delete means two different
                // things with nothing on screen to say which.
                renderCell: (row: UsersListRow): ReactNode =>
                    row.IsPendingDeletion ? (
                        <span className='users-list-username-cell'>
                            <span>{row.Username}</span>
                            <span className='users-list-pending-deletion-tag'>
                                {language.get(UsersListLanguage.UsersList_PendingDeletion_Tag)}
                            </span>
                        </span>
                    ) : (
                        row.Username
                    )
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_FullName),
                field: 'FullName',
                key: 'users-list-fullname',
                sortable: true,
                align: 'left',
                hideBelow: 'sm'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_LockOut),
                field: 'LockOutDisplay',
                key: 'users-list-lockout',
                sortable: true,
                serverSortField: 'LockOut'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_Activity),
                field: 'ListActivity',
                key: 'users-list-activity',
                sortable: true
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_LastSignedIn),
                field: 'ListLastLogon',
                key: 'users-list-last-signed-in',
                sortable: true,
                renderCell: (row: UsersListRow): string =>
                    isStringNotEmpty(row.ListLastLogon) ? row.ListLastLogon : lastLogonNoneLabel
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_ActiveSignIns),
                field: 'ListActiveSignIns',
                key: 'users-list-active-sign-ins',
                sortable: true,
                hideBelow: 'sm'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_UserType),
                field: 'ListPermissionsSummary',
                key: 'users-list-permissions',
                sortable: true,
                hideBelow: 'md'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_TelSys),
                field: 'ListTelSysDescription',
                key: 'users-list-telsys',
                sortable: true,
                hideBelow: 'sm'
            },
            {
                // Sits at the end of the row, as in the design, and carries the activity totals the classic
                // list shows in its Activity column tooltip.
                headerName: EMPTY_STRING,
                field: 'Username',
                key: 'users-list-more-info',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: UsersListRow): ReactElement => (
                    <UsersListMoreInformation username={row.Username} language={language} />
                )
            }
        ]
    }, [language])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    useEffect(getApplicationUsers, [getApplicationUsers])

    return (
        <div className='users-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_users}></PageTitle>
            <div className='users-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL),
                        userTypeLabel: language.get(UsersListLanguage.UsersList_Filter_UserType_Label)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={getApplicationUsers}
                    additionalFilterSkeletonCount={5}
                    additionalFilters={({ isLoading }: AdminFiltersSlotRenderArgs): ReactElement => (
                        <>
                            <div>
                                <SelectFilterMulti
                                    options={userTypeOptions}
                                    label={language.get(UsersListLanguage.UsersList_Filter_UserType_Label)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={userTypeFilters}
                                    onChange={setUserTypeFilters}
                                    formatMultipleSelectionLabel={(count: number): string =>
                                        language.getInterpolated(
                                            UsersListLanguage.UsersList_Filter_SelectedCount,
                                            count
                                        )
                                    }
                                />
                            </div>
                            <div>
                                <SelectFilterMulti
                                    options={licenceOptions}
                                    label={language.get(UsersListLanguage.UsersList_Filter_Licence_Label)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={licenceFilters}
                                    onChange={setLicenceFilters}
                                    formatMultipleSelectionLabel={(count: number): string =>
                                        language.getInterpolated(
                                            UsersListLanguage.UsersList_Filter_SelectedCount,
                                            count
                                        )
                                    }
                                />
                            </div>
                            <div>
                                <SelectFilterMulti
                                    options={activityOptions}
                                    label={language.get(UsersListLanguage.UsersList_Filter_Activity_Label)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={activityFilters}
                                    onChange={setActivityFilters}
                                    formatMultipleSelectionLabel={(count: number): string =>
                                        language.getInterpolated(
                                            UsersListLanguage.UsersList_Filter_SelectedCount,
                                            count
                                        )
                                    }
                                />
                            </div>
                            <div>
                                <SelectFilter
                                    options={lockedOutOptions}
                                    label={language.get(UsersListLanguage.UsersList_Filter_LockedOut_Label)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={lockedOutFilter}
                                    onChange={setLockedOutFilter}
                                    defaultValue={lockedOutOptions[0]?.value}
                                />
                            </div>
                            <div>
                                <SelectFilter
                                    options={pendingDeletionOptions}
                                    label={language.get(UsersListLanguage.UsersList_Filter_PendingDeletion_Label)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={pendingDeletionFilter}
                                    onChange={setPendingDeletionFilter}
                                    defaultValue={pendingDeletionOptions[0]?.value}
                                />
                            </div>
                        </>
                    )}
                />
                <DataTable<UsersListRow, keyof UsersListRow>
                    permissionName={AdminBackendPermissionNames.USER}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        bulkAddButton: language.get(UsersListLanguage.UsersList_BulkAdd_Button),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={usersList ?? []}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad || isWaitingForInitialFiltersLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    onToolbarAddClick={(): void => {
                        setSelectedUsername(undefined)
                        setUserModalViewOnly(false)
                        setUserModalOpen(true)
                    }}
                    onToolbarBulkAddClick={(): void => {
                        setBulkInsertOpen(true)
                    }}
                    onToolbarChangeClick={([selectedItem]: UsersListRow[]): void => {
                        setSelectedUsername(selectedItem.Username)
                        setUserModalViewOnly(false)
                        setUserModalOpen(true)
                    }}
                    onToolbarViewClick={([selectedItem]: UsersListRow[]): void => {
                        setSelectedUsername(selectedItem.Username)
                        setUserModalViewOnly(true)
                        setUserModalOpen(true)
                    }}
                    toolbarCustomActions={toolbarCustomActions}
                    onToolbarDeleteClick={handleToolbarDeleteClick}
                    canToolbarDelete={canToolbarDelete}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isVarNotEmpty(copyReportsSourceUser) ? (
                    <CopyReportsToWizardModal
                        sourceUser={copyReportsSourceUser}
                        onCompleted={refreshUsersList}
                        onExited={(): void => {
                            setCopyReportsSourceUser(undefined)
                        }}
                    />
                ) : (
                    <></>
                )}
                {isBulkInsertOpen ? (
                    <BulkInsertUsers
                        servletPath={bulkInsertServletPath}
                        refreshAction={refreshUsersList}
                        isRouterMode
                        onWizardExited={(): void => {
                            setBulkInsertOpen(false)
                        }}
                    />
                ) : (
                    <></>
                )}
                {isUserModalOpen ? (
                    <AddUserModal
                        key={`${selectedUsername ?? 'new'}-${
                            userModalViewOnly ? 'v' : 'e'
                        }-${filterCMEnvironmentIDForModal}-${modalFilterTelSysID ?? 'any'}-${
                            modalFilterPartitionID ?? 'any'
                        }-${listIncludeRecursive ? 'cmeR' : 'cmeD'}`}
                        filterCMEID={filterCMEnvironmentIDForModal}
                        filterTelSysID={modalFilterTelSysID}
                        initialFilters={{
                            cmeID: filterCMEnvironmentIDForModal,
                            telSysID: modalFilterTelSysID,
                            partitionID: modalFilterPartitionID,
                            includeCmeChildren: listIncludeRecursive
                        }}
                        refreshAction={refreshUsersList}
                        selectedUser={selectedUsername}
                        viewOnly={userModalViewOnly}
                        onModalExited={(): void => {
                            setUserModalOpen(false)
                            setSelectedUsername(undefined)
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
